<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  // FIX: Import getToken to manually attach the authorization header
  import { authFetch, getToken } from '../authService'; 
  import { t } from '../i18nStore';

  let banners = [];
  let isLoading = true;
  let isEditing = false;
  let isUploadingImage = false;
  let error = '';

  // Form State
  let currentBanner = getEmptyBanner();
  let categoriesStr = '';
  let storesStr = '';

  function getEmptyBanner() {
      return {
          name: '',
          imageUrl: '',
          startDate: new Date().toISOString().split('T'),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T'),
          sortingSequence: 10,
          width: 'full',
          countryCode: 'HK',
          isActive: true,
          searchCriteria: { keyword: '', categories: [], stores: [] }
      };
  }

  onMount(async () => {
      fetchBanners();
  });

  async function fetchBanners() {
      isLoading = true;
      try {
          const res = await authFetch(getApiUrl('/admin/banners'));
          if (!res.ok) throw new Error("Failed to load banners");
          banners = await res.json();
      } catch (e) {
          error = e.message;
      } finally {
          isLoading = false;
      }
  }

  function openEditor(banner = null) {
      if (banner) {
          currentBanner = JSON.parse(JSON.stringify(banner));
          // Format dates for input type="date"
          if (currentBanner.startDate) currentBanner.startDate = currentBanner.startDate.split('T');
          if (currentBanner.endDate) currentBanner.endDate = currentBanner.endDate.split('T');
          
          categoriesStr = (currentBanner.searchCriteria.categories || []).join(', ');
          storesStr = (currentBanner.searchCriteria.stores || []).join(', ');
      } else {
          currentBanner = getEmptyBanner();
          categoriesStr = '';
          storesStr = '';
      }
      isEditing = true;
      error = '';
  }

  // --- THE FIX IS HERE ---
  async function handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);
      isUploadingImage = true;

      try {
          const token = await getToken();
          
          // FIX: Use standard fetch instead of authFetch. 
          // This prevents authFetch from incorrectly forcing the 'application/json' Content-Type.
          // The browser will automatically set 'multipart/form-data' with the proper boundary.
          const res = await fetch(getApiUrl('/promotions/upload-image'), {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${token}`
              },
              body: formData
          });
          
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Upload failed");
          
          currentBanner.imageUrl = data.imageUrl;
      } catch (e) {
          alert(e.message);
      } finally {
          isUploadingImage = false;
          event.target.value = '';
      }
  }

  async function saveBanner() {
      if (!currentBanner.name || !currentBanner.imageUrl) {
          return alert("Name and Image URL are required.");
      }

      isLoading = true;
      
      // Parse arrays
      currentBanner.searchCriteria.categories = categoriesStr.split(',').map(s => s.trim()).filter(Boolean);
      currentBanner.searchCriteria.stores = storesStr.split(',').map(s => s.trim()).filter(Boolean);
      
      // Fix dates to ISO strings
      const payload = { ...currentBanner };
      payload.startDate = new Date(payload.startDate).toISOString();
      payload.endDate = new Date(payload.endDate).toISOString();
      payload.sortingSequence = parseInt(payload.sortingSequence) || 0;

      try {
          const method = payload._id ? 'PUT' : 'POST';
          const url = payload._id ? `/admin/banners/${payload._id}` : `/admin/banners`;
          
          // Regular JSON payloads can still safely use authFetch
          const res = await authFetch(getApiUrl(url), {
              method: method,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          if (!res.ok) throw new Error("Failed to save banner");
          
          isEditing = false;
          fetchBanners();
      } catch (e) {
          alert(e.message);
      } finally {
          isLoading = false;
      }
  }

  async function deleteBanner(id) {
      if (!confirm("Are you sure you want to delete this banner?")) return;
      try {
          await authFetch(getApiUrl(`/admin/banners/${id}`), { method: 'DELETE' });
          fetchBanners();
      } catch (e) {
          alert(e.message);
      }
  }
</script>

<div class="admin-container">
  <h2>{$t('admin.banners.title') || 'Banner Management'}</h2>

  {#if isEditing}
      <div class="card form-card">
          <h3>{currentBanner._id ? $t('admin.banners.edit') : $t('admin.banners.add')}</h3>
          
          <form on:submit|preventDefault={saveBanner}>
              <div class="form-row">
                  <div class="form-group">
                      <label>Banner Name</label>
                      <input type="text" bind:value={currentBanner.name} required />
                  </div>
                  <div class="form-group">
                      <label>Country Code</label>
                      <input type="text" bind:value={currentBanner.countryCode} />
                  </div>
              </div>

              <div class="form-group image-upload-box">
                  <label>Banner Image URL</label>
                  <div class="image-flex">
                      <input type="url" bind:value={currentBanner.imageUrl} required placeholder="https://..." />
                      <div class="upload-btn-wrapper">
                          <button type="button" class="btn secondary" disabled={isUploadingImage}>
                              {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                          </button>
                          <input type="file" accept="image/*" on:change={handleImageUpload} />
                      </div>
                  </div>
                  {#if currentBanner.imageUrl}
                      <img src={currentBanner.imageUrl} alt="Preview" class="preview-img" />
                  {/if}
              </div>

              <div class="form-row">
                  <div class="form-group">
                      <label>Start Date</label>
                      <input type="date" bind:value={currentBanner.startDate} required />
                  </div>
                  <div class="form-group">
                      <label>End Date</label>
                      <input type="date" bind:value={currentBanner.endDate} required />
                  </div>
              </div>

              <div class="form-row">
                  <div class="form-group">
                      <label>Sorting Sequence (Lower = First)</label>
                      <input type="number" bind:value={currentBanner.sortingSequence} />
                  </div>
                  <div class="form-group">
                      <label>Desktop Width</label>
                      <select bind:value={currentBanner.width}>
                          <option value="full">Full Screen (100%)</option>
                          <option value="half">Half Screen (50%)</option>
                          <option value="quarter">Quarter Screen (25%)</option>
                      </select>
                  </div>
              </div>

              <fieldset>
                  <legend>Click Action: Search Criteria</legend>
                  <p class="help-text">When a user clicks this banner, they will search for the following:</p>
                  
                  <div class="form-group">
                      <label>Search Keyword</label>
                      <input type="text" bind:value={currentBanner.searchCriteria.keyword} placeholder="e.g. Summer Sale" />
                  </div>
                  <div class="form-row">
                      <div class="form-group">
                          <label>Categories (Comma separated)</label>
                          <input type="text" bind:value={categoriesStr} placeholder="e.g. Flowers, Gifts" />
                      </div>
                      <div class="form-group">
                          <label>Stores (Comma separated)</label>
                          <input type="text" bind:value={storesStr} placeholder="e.g. ParknShop, TASTE" />
                      </div>
                  </div>
              </fieldset>

              <div class="form-group checkbox-group">
                  <input type="checkbox" id="isActive" bind:checked={currentBanner.isActive} />
                  <label for="isActive">Banner is Active</label>
              </div>

              <div class="actions">
                  <button type="button" class="btn cancel" on:click={() => isEditing = false}>Cancel</button>
                  <button type="submit" class="btn primary" disabled={isLoading}>Save Banner</button>
              </div>
          </form>
      </div>
  {:else}
      <button class="btn primary mb" on:click={() => openEditor(null)}>+ Add New Banner</button>

      {#if isLoading}
          <p>Loading banners...</p>
      {:else}
          <div class="table-responsive">
              <table>
                  <thead>
                      <tr>
                          <th>Preview</th>
                          <th>Name</th>
                          <th>Dates</th>
                          <th>Width</th>
                          <th>Sort Seq</th>
                          <th>Status</th>
                          <th>Actions</th>
                      </tr>
                  </thead>
                  <tbody>
                      {#each banners as banner}
                          <tr>
                              <td><img src={banner.imageUrl} alt="preview" class="table-thumb" /></td>
                              <td>{banner.name}</td>
                              <td>{new Date(banner.startDate).toLocaleDateString()} - <br/>{new Date(banner.endDate).toLocaleDateString()}</td>
                              <td>{banner.width}</td>
                              <td>{banner.sortingSequence}</td>
                              <td>
                                  <span class="badge {banner.isActive ? 'active' : 'inactive'}">
                                      {banner.isActive ? 'Active' : 'Inactive'}
                                  </span>
                              </td>
                              <td>
                                  <button class="btn small" on:click={() => openEditor(banner)}>Edit</button>
                                  <button class="btn small danger" on:click={() => deleteBanner(banner._id)}>Delete</button>
                              </td>
                          </tr>
                      {/each}
                  </tbody>
              </table>
          </div>
      {/if}
  {/if}
</div>

<style>
  .admin-container { max-width: 1000px; margin: 2rem auto; padding: 1rem; }
  .card { background: #fff; padding: 2rem; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
  
  .form-group { margin-bottom: 1.5rem; }
  .form-group label { display: block; font-weight: 600; margin-bottom: 0.5rem; color: #444; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  
  input[type="text"], input[type="number"], input[type="url"], input[type="date"], select {
      width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; font-size: 1rem;
  }
  
  .image-flex { display: flex; gap: 1rem; }
  .upload-btn-wrapper { position: relative; overflow: hidden; display: inline-block; white-space: nowrap; }
  .upload-btn-wrapper input[type=file] { font-size: 100px; position: absolute; left: 0; top: 0; opacity: 0; cursor: pointer; height: 100%; width: 100%; }
  
  .preview-img { max-height: 150px; border-radius: 8px; margin-top: 1rem; border: 1px solid #eee; }
  .table-thumb { width: 80px; height: 40px; object-fit: cover; border-radius: 4px; }
  
  fieldset { border: 1px solid #e0e0e0; border-radius: 6px; padding: 1.5rem; margin-bottom: 1.5rem; }
  legend { font-weight: bold; padding: 0 0.5rem; color: #007bff; }
  .help-text { color: #666; font-size: 0.9rem; margin-top: 0; margin-bottom: 1rem; }
  
  .checkbox-group { display: flex; align-items: center; gap: 0.5rem; }
  .checkbox-group input { width: auto; transform: scale(1.2); }
  
  .btn { padding: 0.75rem 1.5rem; border: none; border-radius: 4px; font-weight: bold; cursor: pointer; transition: opacity 0.2s; }
  .btn.primary { background: #007bff; color: white; }
  .btn.secondary { background: #6c757d; color: white; }
  .btn.cancel { background: #e0e0e0; color: #333; }
  .btn.danger { background: #dc3545; color: white; }
  .btn.small { padding: 0.4rem 0.8rem; font-size: 0.9rem; }
  .btn:hover { opacity: 0.8; }
  .mb { margin-bottom: 1.5rem; }

  .actions { display: flex; justify-content: flex-end; gap: 1rem; border-top: 1px solid #eee; padding-top: 1.5rem; }
  
  table { width: 100%; border-collapse: collapse; background: white; border-radius: 8px; overflow: hidden; }
  th, td { padding: 1rem; border-bottom: 1px solid #eee; text-align: left; }
  th { background: #f8f9fa; font-weight: 600; }
  .badge { padding: 0.3rem 0.6rem; border-radius: 20px; font-size: 0.85rem; font-weight: bold; }
  .badge.active { background: #d4edda; color: #155724; }
  .badge.inactive { background: #f8d7da; color: #721c24; }
</style>